import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Cta from '../../components/Cta';
import { sectors } from './sectorsData';
import { useLanguage } from '../../context/LanguageContext';
import { waLink } from '../../utils/whatsapp';

const pad = (n) => String(n).padStart(2, '0');

export default function SectorPage({ data }) {
  const { isEN, t } = useLanguage();
  const others = sectors.filter((s) => s.slug !== data.slug);

  useEffect(() => {
    document.title = t(data.metaTitle);
  }, [data, isEN, t]);

  return (
    <>
      <section className="page-hero page-hero--slides">
        <div className="page-hero__bg">
          <div className="hero-slides">
            {data.heroSlides.map((src, i) => (
              <div
                key={src}
                className={'hero-slide' + (i === 0 ? ' is-active' : '')}
                role="img"
                aria-label={t(data.navLabel)}
                style={{ backgroundImage: `url('${src}')` }}
              ></div>
            ))}
          </div>
        </div>
        <div className="page-hero__glow"></div>
        <div className="wrap">
          <nav className="breadcrumb" data-reveal="fade">
            <Link to="/">{isEN ? 'Home' : 'الرئيسية'}</Link><span>/</span><span>{isEN ? 'Elhaware Group' : 'مجموعة الهواري'}</span><span>/</span>
            <span style={{ color: 'rgba(255,255,255,.85)' }}>{t(data.navLabel)}</span>
          </nav>
          <span className="eyebrow" data-reveal>{isEN ? 'Elhaware Group' : 'مجموعة الهواري'}</span>
          <h1 data-reveal data-delay="1">{t(data.navLabel)}</h1>
          <p className="page-hero__lead" data-reveal data-delay="2">{t(data.heroLead)}</p>
        </div>
      </section>

      <section className="sec-intro section">
        <div className="wrap sec-intro__grid">
          <div className="sec-intro__body" data-reveal="right">
            <span className="eyebrow">{isEN ? 'About the Sector' : 'عن القطاع'}</span>
            <h2 style={{ fontSize: 'clamp(26px,3.4vw,42px)', fontWeight: 300, color: 'var(--forest-900)', margin: '16px 0 26px', lineHeight: 1.2 }}>
              {t(data.navLabel)}
            </h2>
            {data.introParas.map((p, i) => <p key={i}>{t(p)}</p>)}
          </div>
          <div className="sec-intro__media" data-reveal="left">
            <img className="sec-intro__img" loading="lazy" src={data.introImg} alt={t(data.navLabel)} />
            <div className="sec-intro__badge"><b className="num-latin">{t(data.badge.big)}</b><span>{t(data.badge.text)}</span></div>
          </div>
        </div>
      </section>

      <section className="sec-div section">
        <div className="wrap">
          <div className="shead" data-reveal>
            <span className="eyebrow">{isEN ? 'Sector Companies' : 'شركات القطاع'}</span>
            <h2>{isEN ? 'Entities operating within' : 'الكيانات العاملة ضمن'} <b>{t(data.navLabel)}</b></h2>
          </div>
          <div className="sec-div__grid">
            {data.companies.map((c, i) => (
              <article className={`div-card${c.logo ? ' div-card--logo' : ''}`} data-reveal data-delay={String(i + 1)} key={i}>
                <div className="div-card__media">
                  <span className="div-card__idx num-latin">{pad(i + 1)}</span>
                  <img className="div-card__img" loading="lazy" src={c.img} alt={t(c.title)} />
                </div>
                <div className="div-card__body">
                  <h3 className="div-card__title">{t(c.title)}</h3>
                  <p className="div-card__desc">{t(c.desc)}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {data.molasses && (
        <section className="sec-molasses molasses-a section">
          <div className="wrap molasses-a__grid">
            <div data-reveal="right">
              <span className="eyebrow">{t(data.molasses.eyebrow)}</span>
              <h2 className="molasses-a__title">{t(data.molasses.title.before)}<b>{t(data.molasses.title.bold)}</b></h2>
              <p className="molasses-a__lead">{t(data.molasses.lead)}</p>
              <ul className="molasses-a__facts">
                {data.molasses.facts.map((f, i) => <li key={i}>{t(f)}</li>)}
              </ul>
            </div>
            <div className="molasses-a__media" data-reveal="left">
              <img className="molasses-a__img" loading="lazy" src={`/assets/img/${data.molasses.img}`} alt={isEN ? 'Molasses — converted black honey' : 'المولاس — العسل الأسود المحوّل الصناعي'} />
              {data.molasses.overlayImg && <img className="molasses-a__overlay" loading="lazy" src={`/assets/img/${data.molasses.overlayImg}`} alt={isEN ? 'Sugarcane before the molasses process' : 'قصب السكر قبل عملية تحويل المولاس'} />}
              <div className="molasses-a__badge"><b className="num-latin">{t(data.molasses.badge.big)}</b><span>{t(data.molasses.badge.text)}</span></div>
            </div>
          </div>
        </section>
      )}

      {data.crops && (
        <section className="sec-crops crops-a section">
          <div className="wrap">
            <div className="crops-a__head" data-reveal>
              <div>
                <span className="eyebrow">{isEN ? 'Sector Crops' : 'محاصيل القطاع'}</span>
                <h2>{isEN ? 'What we grow from the ' : 'ما نزرعه من '}<b>{isEN ? 'heart of the land' : 'قلب الأرض'}</b></h2>
              </div>
              <p className="crops-a__intro">{isEN ? 'A selection of premium-quality fruit & Vegetables produced on the group’s farms and packed fresh for the local market and export.' : 'نخبةٌ من الفاكهة و الخضروات عالية الجودة تُنتَج وتُعبَّأ طازجةً للسوق المحلي والتصدير.'}</p>
            </div>
            <div className="crops-grid">
              {data.crops.map((c, i) => (
                <article className="crop-card" style={{ '--crop-accent': c.accent }} data-reveal data-delay={String((i % 3) + 1)} key={c.key}>
                  <div className="crop-card__media">
                    <img className="crop-card__img" loading="lazy" src={`/assets/img/${c.img}`} alt={isEN ? c.en : c.name} />
                    <span className="crop-card__tag">{isEN ? c.name : c.en}</span>
                  </div>
                  <div className="crop-card__body">
                    <h3 className="crop-card__name">{isEN ? c.en : c.name}</h3>
                    <p className="crop-card__desc">{t(c.desc)}</p>
                    <a
                      className="crop-card__order"
                      href={waLink(isEN ? `Hello Elhaware Group, I'd like to order ${c.en}.` : `مرحباً مجموعة الهواري، أودّ طلب ${c.name}.`)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {isEN ? 'Contact us to order' : 'تواصل معنا للطلب'} <span className="arr">←</span>
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {data.packing && (
        <section className="sec-packing packing-a section">
          <div className="wrap">
            <div className="crops-a__head" data-reveal>
              <div>
                <span className="eyebrow">{t(data.packing.eyebrow)}</span>
                <h2>{t(data.packing.title.before)}<b>{t(data.packing.title.bold)}</b></h2>
              </div>
              <p className="crops-a__intro">{t(data.packing.lead)}</p>
            </div>
            <div className="packing-a__gallery">
              {data.packing.photos.map((p) => (
                <figure className={'pack-shot ' + (p.wide ? 'pack-shot--wide' : 'pack-shot--third')} data-reveal key={p.img}>
                  <img loading="lazy" src={`/assets/img/${p.img}`} alt={t(p.cap)} />
                  <figcaption className="pack-shot__cap">{t(p.cap)}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="sec-more section">
        <div className="wrap">
          <div className="shead" data-reveal>
            <span className="eyebrow">{isEN ? 'Browse Sectors' : 'تصفّح القطاعات'}</span>
            <h2 style={{ color: '#fff' }}>{isEN ? 'Other sectors within' : 'قطاعاتٌ أخرى ضمن'} <b style={{ color: 'var(--champagne)' }}>{isEN ? 'Elhaware Group' : 'مجموعة الهواري'}</b></h2>
          </div>
          <div className="sec-more__grid">
            {others.map((s, i) => (
              <Link className="sec-more__item" to={s.route} data-reveal data-delay={String(i + 1)} key={s.slug}>
                <span>{pad(i + 1)}</span><b>{t(s.navLabel)}</b>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Cta />
    </>
  );
}
